import type { TechnicalDataScenario } from "features/technical-data/scenario/model/types";

import { useGetTrScenarios } from "features/technical-data/scenario/api/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import type { ScenarioDefinitionFormState, ScenarioDefinitionPayload } from "../model/transientScenario";

import { mapTrScenarioToFormState } from "../lib/mapTrScenarioToFormState";
import { buildScenarioDefinitionPayload } from "../model/transientScenario";

const mapScenarios = (items: TechnicalDataScenario[] | undefined) => {
  if (!items) return [];
  const mapped = items.map(mapTrScenarioToFormState);
  const sharedWeatherYear = mapped[0]?.weather_data_year;
  if (sharedWeatherYear == null) return mapped;
  return mapped.map((scenario) => ({ ...scenario, weather_data_year: sharedWeatherYear }));
};

export const useTransientScenarios = () => {
  const { projectId } = useParams();
  const { data, isLoading, refetch } = useGetTrScenarios(projectId);
  const [scenarios, setScenarios] = useState<ScenarioDefinitionFormState[]>([]);

  useEffect(() => {
    if (!data) return;
    setScenarios(mapScenarios(data));
  }, [data]);

  const updateScenario = useCallback((scenarioId: number, patch: Partial<ScenarioDefinitionFormState>) => {
    setScenarios((current) =>
      current.map((scenario) => (scenario.id === scenarioId ? { ...scenario, ...patch } : scenario))
    );
  }, []);

  const updateWeatherDataYear = useCallback((weatherDataYear: number) => {
    setScenarios((current) => current.map((scenario) => ({ ...scenario, weather_data_year: weatherDataYear })));
  }, []);

  const reset = useCallback(() => {
    setScenarios(mapScenarios(data));
  }, [data]);

  const getPayload = useCallback(
    (): ScenarioDefinitionPayload[] => scenarios.map((scenario) => buildScenarioDefinitionPayload(scenario)),
    [scenarios]
  );

  return useMemo(
    () => ({
      getPayload,
      isLoading,
      refetch,
      reset,
      scenarios,
      updateScenario,
      updateWeatherDataYear,
    }),
    [getPayload, isLoading, refetch, reset, scenarios, updateScenario, updateWeatherDataYear]
  );
};

export type UseTransientScenariosReturn = ReturnType<typeof useTransientScenarios>;
